import { useCallback, useEffect, useState } from "react";
import { Search, Eye, CheckCircle2, XCircle, Ban, RotateCcw, Percent, Banknote, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import { StatCard } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, Pill, DataTable, FilterTabs, SectionTitle, Field, fmtDate, fmtDateTime } from "@/components/dash";

const useLoader = (fn, deps = []) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => fn().then(setData).catch((e) => toast.error(apiError(e))), deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

function KV({ k, v }) {
  return <div><dt className="text-xs text-muted-foreground">{k}</dt><dd className="text-sm font-medium break-words">{v || "—"}</dd></div>;
}

/* ------------------------------------------------------------------ requests */
export function RequestsPanel({ isSuper, onChanged }) {
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [data, reload] = useLoader(
    () => api.get("/admin/vendor-applications", { params: { status, q: q || undefined } }).then((r) => r.data), [status, q]);
  const [open, setOpen] = useState(null);       // detailed application
  const [mode, setMode] = useState("view");     // view | approve | reject
  const [note, setNote] = useState("");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  const openApp = async (id) => {
    try {
      const { data: d } = await api.get(`/admin/vendor-applications/${id}`);
      setOpen(d); setMode("view"); setNote(""); setRate("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (kind) => {
    setBusy(true);
    try {
      if (kind === "approve") {
        await api.post(`/admin/vendor-applications/${open.id}/approve`, { note, commission_rate: rate === "" ? null : parseFloat(rate) });
        toast.success(`${open.store_name} approved`);
      } else {
        await api.post(`/admin/vendor-applications/${open.id}/reject`, { note });
        toast.success("Application rejected — the seller has been notified");
      }
      setOpen(null); reload(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const counts = data?.counts || {};
  return (
    <div data-testid="requests-panel">
      <SectionTitle title="Seller requests" desc="Review new seller applications. Only a Super Admin can approve or reject." />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <FilterTabs value={status} onChange={setStatus} options={[
          { key: "pending", label: "Pending", count: counts.pending }, { key: "approved", label: "Approved", count: counts.approved },
          { key: "rejected", label: "Rejected", count: counts.rejected }, { key: "suspended", label: "Suspended", count: counts.suspended },
          { key: "all", label: "All", count: counts.all }]} />
        <div className="relative w-full sm:w-64 mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search store, email, PAN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="requests-search" />
        </div>
      </div>
      <DataTable testid="requests-table" empty={status === "pending" ? "No applications waiting for review." : "No applications found."}
        head={["Store", "Owner", "Location", "PAN", "Applied", "Status", "Actions"]}
        rows={(data?.items || []).map((a) => [
          <div><p className="font-semibold">{a.store_name}</p><p className="text-xs text-muted-foreground capitalize">{a.business_type?.replace("_", " ")}</p></div>,
          <div><p>{a.owner_name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>,
          `${a.city}, ${a.state}`, <span className="font-mono text-xs">{a.pan_number}</span>, fmtDate(a.submitted_at), <StatusPill status={a.status} />,
          <Button size="sm" variant={a.status === "pending" ? "default" : "outline"} onClick={() => openApp(a.id)} data-testid={`review-${a.id}`}><Eye className="h-4 w-4 mr-1" /> Review</Button>,
        ])} />

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{open.store_name} <StatusPill status={open.status} /></DialogTitle>
                <DialogDescription>Applied {fmtDateTime(open.submitted_at)} by {open.owner_name} ({open.email})</DialogDescription>
              </DialogHeader>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                <KV k="Business type" v={open.business_type?.replace("_", " ")} /><KV k="Contact number" v={open.contact_phone} />
                <KV k="PAN" v={open.pan_number} /><KV k="GSTIN" v={open.gst_number || "Not provided"} />
                <div className="sm:col-span-2"><KV k="Address" v={`${open.address_line1}, ${open.city}, ${open.state} ${open.pincode}`} /></div>
                <div className="sm:col-span-2"><KV k="Selling in" v={(open.categories || []).join(", ") || "—"} /></div>
                {open.description && <div className="sm:col-span-2"><KV k="About" v={open.description} /></div>}
              </dl>
              <div className="rounded-lg bg-slate-50 border border-border p-3 mt-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Bank details {!isSuper && "(masked)"}</p>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  <KV k="Account holder" v={open.bank?.account_holder} /><KV k="Bank" v={open.bank?.bank_name} />
                  <KV k="Account number" v={open.bank?.account_number} /><KV k="IFSC" v={open.bank?.ifsc} />
                </dl>
              </div>
              {open.review_note && <p className="text-sm rounded-lg bg-amber-50 border border-amber-200 p-3"><b>Last note:</b> {open.review_note}</p>}
              {open.history?.length > 1 && (
                <details className="text-sm"><summary className="cursor-pointer text-muted-foreground">History ({open.history.length})</summary>
                  <ul className="mt-2 space-y-1">{[...open.history].reverse().map((h, i) => <li key={i} className="text-xs text-muted-foreground"><b className="capitalize text-foreground">{h.status}</b> · {fmtDateTime(h.at)} · {h.by}{h.note ? ` · ${h.note}` : ""}</li>)}</ul>
                </details>
              )}

              {mode === "approve" && (
                <div className="space-y-3 border-t border-border pt-4">
                  <Field label="Commission rate for this seller (%)" hint="Leave blank to use the marketplace default."><Input type="number" min="0" max="50" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="approve-rate" /></Field>
                  <Field label="Note to seller (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
                </div>
              )}
              {mode === "reject" && (
                <div className="space-y-3 border-t border-border pt-4">
                  <Field label="Reason for rejection" hint="The seller sees this and can fix and resubmit."><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. PAN and bank account holder names don't match" data-testid="reject-note" /></Field>
                </div>
              )}

              <DialogFooter className="gap-2">
                {!isSuper && <p className="text-xs text-muted-foreground mr-auto self-center">Only a Super Admin can approve or reject.</p>}
                {isSuper && mode === "view" && ["pending", "rejected"].includes(open.status) && (
                  <>
                    {open.status === "pending" && <Button variant="outline" className="text-destructive" onClick={() => setMode("reject")} data-testid="reject-btn"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>}
                    <Button onClick={() => setMode("approve")} data-testid="approve-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                  </>
                )}
                {mode === "approve" && <><Button variant="outline" onClick={() => setMode("view")}>Back</Button><Button disabled={busy} onClick={() => decide("approve")} data-testid="approve-confirm">Confirm approval</Button></>}
                {mode === "reject" && <><Button variant="outline" onClick={() => setMode("view")}>Back</Button><Button variant="destructive" disabled={busy || note.trim().length < 5} onClick={() => decide("reject")} data-testid="reject-confirm">Reject application</Button></>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ vendors */
export function VendorsPanel({ isSuper, onChanged }) {
  const [rows, reload] = useLoader(() => api.get("/admin/vendors").then((r) => r.data));
  const [act, setAct] = useState(null);   // {type: suspend|commission, v}
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      if (act.type === "suspend") await api.post(`/admin/vendors/${act.v.id}/suspend`, { note: val });
      if (act.type === "commission") await api.put(`/admin/vendors/${act.v.id}/commission`, { commission_rate: val === "" ? null : parseFloat(val) });
      toast.success("Updated"); setAct(null); reload(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const reinstate = async (v) => {
    try { await api.post(`/admin/vendors/${v.id}/reinstate`); toast.success(`${v.store_name} reinstated`); reload(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleActive = async (v) => {
    try { await api.put(`/admin/users/${v.id}/active`, { active: !v.active }); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="vendors-panel">
      <SectionTitle title="Vendors" desc="Every seller on the marketplace with sales, commission and balance at a glance." />
      <DataTable testid="admin-vendors-table" empty="No vendors yet." head={["Store", "Status", "Products", "Gross sales", "Net earnings", "Available", "Commission", "Actions"]}
        rows={(rows || []).map((v) => [
          <div><p className="font-semibold">{v.store_name}</p><p className="text-xs text-muted-foreground">{v.email}</p></div>,
          <div className="flex flex-col gap-1 items-start"><StatusPill status={v.vendor_status} />{!v.active && <Pill tone="red">login disabled</Pill>}</div>,
          <span>{v.products}{v.pending_products > 0 && <Pill tone="amber" className="ml-1">{v.pending_products} in review</Pill>}</span>,
          formatINR(v.gross_sales), formatINR(v.net_earnings), <b>{formatINR(v.available_balance)}</b>,
          <span>{v.commission_rate}%{v.custom_commission && <span className="text-xs text-muted-foreground"> (custom)</span>}</span>,
          <div className="flex gap-1 justify-end flex-wrap">
            {isSuper && v.vendor_status === "approved" && <Button size="sm" variant="ghost" title="Set commission" onClick={() => { setVal(v.custom_commission ? String(v.commission_rate) : ""); setAct({ type: "commission", v }); }} data-testid={`commission-${v.id}`}><Percent className="h-4 w-4" /></Button>}
            {isSuper && v.vendor_status === "approved" && <Button size="sm" variant="ghost" className="text-destructive" title="Suspend seller" onClick={() => { setVal(""); setAct({ type: "suspend", v }); }} data-testid={`suspend-${v.id}`}><Ban className="h-4 w-4" /></Button>}
            {isSuper && v.vendor_status === "suspended" && <Button size="sm" variant="outline" onClick={() => reinstate(v)} data-testid={`reinstate-${v.id}`}><RotateCcw className="h-4 w-4 mr-1" /> Reinstate</Button>}
            <Button size="sm" variant="ghost" title={v.active ? "Disable login" : "Enable login"} onClick={() => toggleActive(v)}>{v.active ? <Store className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}</Button>
          </div>,
        ])} />

      <Dialog open={!!act} onOpenChange={(v) => !v && setAct(null)}>
        <DialogContent data-testid="vendor-action-dialog">
          {act && (
            <>
              <DialogHeader><DialogTitle>{act.type === "suspend" ? `Suspend ${act.v.store_name}?` : `Commission for ${act.v.store_name}`}</DialogTitle>
                <DialogDescription>{act.type === "suspend" ? "All of this seller's products are hidden immediately and they can't manage orders until reinstated." : "Applies to new orders only; existing orders keep the rate they were placed with."}</DialogDescription></DialogHeader>
              {act.type === "suspend"
                ? <Field label="Reason (the seller will see this)"><Textarea rows={3} value={val} onChange={(e) => setVal(e.target.value)} data-testid="suspend-note" /></Field>
                : <Field label="Commission rate (%)" hint="Leave blank to use the marketplace default."><Input type="number" min="0" max="50" step="0.5" value={val} onChange={(e) => setVal(e.target.value)} data-testid="commission-input" /></Field>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setAct(null)}>Cancel</Button>
                <Button variant={act.type === "suspend" ? "destructive" : "default"} disabled={busy || (act.type === "suspend" && val.trim().length < 5)} onClick={run} data-testid="vendor-action-confirm">{act.type === "suspend" ? "Suspend seller" : "Save"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ payouts */
export function PayoutsPanel({ isSuper, onChanged }) {
  const [vendors, reloadV] = useLoader(() => api.get("/admin/vendors").then((r) => r.data));
  const [payouts, reloadP] = useLoader(() => api.get("/admin/payouts").then((r) => r.data));
  const [stats, reloadS] = useLoader(() => api.get("/admin/marketplace-stats").then((r) => r.data));
  const [pay, setPay] = useState(null);
  const [f, setF] = useState({ amount: "", reference: "", method: "bank_transfer", note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vendors/${pay.id}/payouts`, { ...f, amount: parseFloat(f.amount) });
      toast.success("Payout recorded"); setPay(null); reloadV(); reloadP(); reloadS(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const due = (vendors || []).filter((v) => v.available_balance > 0);
  return (
    <div className="space-y-6" data-testid="payouts-panel">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Payable to sellers now" value={formatINR(stats?.payouts_due)} icon={Banknote} />
        <StatCard label="Paid out to date" value={formatINR(stats?.payouts_total)} icon={Banknote} />
        <StatCard label="Commission earned" value={formatINR(stats?.commission_earned)} icon={Percent} />
      </div>
      <div>
        <SectionTitle title="Due for payout" desc="Earnings from delivered orders that have cleared the return-hold window. Transfer the money from your bank, then record it here so the seller's balance stays accurate." />
        <DataTable empty="No seller has a payable balance right now." head={["Seller", "Bank", "Available", "Actions"]}
          rows={due.map((v) => [<div><p className="font-semibold">{v.store_name}</p><p className="text-xs text-muted-foreground">{v.email}</p></div>,
            <Button size="sm" variant="link" className="p-0 h-auto" onClick={async () => { try { const { data } = await api.get(`/admin/vendor-applications/${v.application_id}`); toast.info(`${data.bank?.account_holder} · ${data.bank?.bank_name} · A/C ${data.bank?.account_number} · ${data.bank?.ifsc}`, { duration: 15000 }); } catch (e) { toast.error(apiError(e)); } }} disabled={!v.application_id || !isSuper}>{isSuper ? "Show bank details" : "—"}</Button>,
            <b>{formatINR(v.available_balance)}</b>,
            isSuper ? <Button size="sm" onClick={() => { setF({ amount: String(v.available_balance), reference: "", method: "bank_transfer", note: "" }); setPay(v); }} data-testid={`pay-${v.id}`}>Record payout</Button> : <span className="text-xs text-muted-foreground">Super Admin only</span>])} />
      </div>
      <div>
        <SectionTitle title="Payout history" />
        <DataTable empty="No payouts recorded yet." head={["Date", "Seller", "Amount", "Method", "Reference", "By"]}
          rows={(payouts || []).map((p) => [fmtDateTime(p.created_at), p.vendor_name, <b>{formatINR(p.amount)}</b>, p.method.replace("_", " "), <span className="font-mono text-xs">{p.reference}</span>, p.created_by])} />
      </div>

      <Dialog open={!!pay} onOpenChange={(v) => !v && setPay(null)}>
        <DialogContent data-testid="payout-dialog">
          {pay && (
            <>
              <DialogHeader><DialogTitle>Record payout to {pay.store_name}</DialogTitle><DialogDescription>Available balance: {formatINR(pay.available_balance)}</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <Field label="Amount (₹)"><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payout-amount" /></Field>
                <Field label="Method">
                  <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[["bank_transfer", "Bank transfer (NEFT/IMPS/RTGS)"], ["upi", "UPI"], ["cheque", "Cheque"], ["other", "Other"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Transaction reference (UTR / cheque no.)"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="payout-ref" /></Field>
                <Field label="Note (optional)"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setPay(null)}>Cancel</Button><Button disabled={busy || !f.amount || f.reference.trim().length < 3} onClick={submit} data-testid="payout-confirm">Record payout</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
